/**
 * AuthContext - 全局认证状态管理
 * 提供用户登录状态、token 管理、登录/登出方法
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as C from '../constants';
import localCacheService from '../services/localCacheService';
import dashboardCacheService from '../services/dashboardCacheService';
import { resetIdleAssetWarmupState } from '../services/idleAssetWarmup';

const AuthContext = createContext(null);

// Token 存储 key
const TOKEN_KEY = 'cineart_token';
const USER_KEY = 'cineart_user';
// 🆕 会话模式标记（管理员登录到用户时使用 sessionStorage）
const SESSION_MODE_KEY = 'cineart_session_mode';
// 🆕 临时 token 处理锁（防止 Strict Mode 重复请求）
const TEMP_TOKEN_LOCK_KEY = 'cineart_temp_token_processing';

// 🆕 根据模式选择存储（sessionStorage 或 localStorage）
const getStorage = () => {
  // 如果 sessionStorage 中有 session_mode 标记，使用 sessionStorage
  if (sessionStorage.getItem(SESSION_MODE_KEY)) {
    return sessionStorage;
  }
  return localStorage;
};

/**
 * AuthProvider - 包裹整个应用，提供认证状态
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true); // 初始化加载状态
  const [isSessionMode, setIsSessionMode] = useState(false); // 🆕 会话模式标记

  // 登出
  const logout = useCallback(() => {
    const prevUserId = user?.id;
    setUser(null);
    setToken(null);
    // 🆕 根据模式清理对应的存储
    const storage = getStorage();
    storage.removeItem(TOKEN_KEY);
    storage.removeItem(USER_KEY);
    // 清理会话模式标记
    sessionStorage.removeItem(SESSION_MODE_KEY);
    setIsSessionMode(false);
    // 🆕 清理缓存服务的用户状态
    localCacheService.setCurrentUser(null);
    // 🛡️ L4: 释放前一用户的内存缓存 blob URL，防止泄漏/隐私残留
    localCacheService.clearBlobUrlCache();
    // 🆕 清理仪表盘列表缓存（projects/crews/assets 快照）
    if (prevUserId) dashboardCacheService.clearForUser(prevUserId);
    // 🆕 重置 idle 预热状态，下个用户登录后重新预热缩略图等
    resetIdleAssetWarmupState();
  }, [user?.id]);

  // 初始化：从 localStorage/sessionStorage 恢复登录状态，或处理临时 token
  useEffect(() => {
    // 🆕 防止 React Strict Mode 下重复执行
    let isCancelled = false;
    
    const initAuth = async () => {
      // 🆕 调试：检查当前 URL
      
      // 🆕 优先检查 sessionStorage（可能是 Strict Mode 第二次执行，数据已保存）
      if (sessionStorage.getItem(SESSION_MODE_KEY)) {
        const savedToken = sessionStorage.getItem(TOKEN_KEY);
        const savedUser = sessionStorage.getItem(USER_KEY);
        
        if (savedToken && savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setIsSessionMode(true);
            setToken(savedToken);
            setUser(parsedUser);
            localCacheService.setCurrentUser(parsedUser.id);
            // 清除 URL 参数（如果有的话）
            if (window.location.search) {
              window.history.replaceState({}, '', window.location.pathname);
            }
            setLoading(false);
            return;
          } catch (e) {
            console.error('解析会话用户失败:', e);
            sessionStorage.removeItem(SESSION_MODE_KEY);
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(USER_KEY);
          }
        }
      }
      
      // 🆕 检查 URL 参数中的临时 token
      const urlParams = new URLSearchParams(window.location.search);
      const tempToken = urlParams.get('temp_token');

      if (tempToken) {
        // 🆕 检查是否已有其他执行在处理
        if (sessionStorage.getItem(TEMP_TOKEN_LOCK_KEY)) {
          // 等待一小段时间让另一个执行完成
          await new Promise(resolve => setTimeout(resolve, 500));
          // 再次检查 sessionStorage
          if (sessionStorage.getItem(SESSION_MODE_KEY)) {
            const savedToken = sessionStorage.getItem(TOKEN_KEY);
            const savedUser = sessionStorage.getItem(USER_KEY);
            if (savedToken && savedUser) {
              const parsedUser = JSON.parse(savedUser);
              setIsSessionMode(true);
              setToken(savedToken);
              setUser(parsedUser);
              localCacheService.setCurrentUser(parsedUser.id);
              window.history.replaceState({}, '', window.location.pathname);
              setLoading(false);
              return;
            }
          }
          setLoading(false);
          return;
        }
        
        // 🆕 设置锁
        sessionStorage.setItem(TEMP_TOKEN_LOCK_KEY, 'true');
        
        try {
          // 用临时 token 换取正式 token
          const res = await fetch(`${C.API_BASE_URL}/api/admin/exchange-temp-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tempToken })
          });
          
          const data = await res.json();
          
          // 🆕 清除锁
          sessionStorage.removeItem(TEMP_TOKEN_LOCK_KEY);
          
          if (res.ok && data.success) {
            // 🆕 成功后才清除 URL 参数
            window.history.replaceState({}, '', window.location.pathname);
            
            // 使用 sessionStorage 存储（会话模式）
            sessionStorage.setItem(SESSION_MODE_KEY, 'true');
            sessionStorage.setItem(TOKEN_KEY, data.token);
            sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
            
            // 🆕 如果组件已卸载（Strict Mode），不更新状态，但数据已保存到 sessionStorage
            if (isCancelled) {
              return;
            }
            
            setIsSessionMode(true);
            setToken(data.token);
            setUser(data.user);
            localCacheService.setCurrentUser(data.user.id);
            setLoading(false);
            return;
          } else {
            console.error('[AuthContext] 临时 token 换取失败:', data.error);
            // 清除 URL 参数
            window.history.replaceState({}, '', window.location.pathname);
            if (!isCancelled) setLoading(false);
            return;
          }
        } catch (err) {
          console.error('[AuthContext] 换取 token 网络错误:', err);
          // 🆕 清除锁
          sessionStorage.removeItem(TEMP_TOKEN_LOCK_KEY);
          if (!isCancelled) setLoading(false);
          return;
        }
      }
      
      // 🆕 普通模式：从 localStorage 恢复
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);
      
      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setToken(savedToken);
          setUser(parsedUser);
          
          // 设置缓存服务的当前用户
          localCacheService.setCurrentUser(parsedUser.id);
          
          // 验证 token 并获取最新用户信息（同步积分等信息）
          fetch(`${C.API_BASE_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${savedToken}` }
          }).then(res => {
            if (res.ok) return res.json();
            throw new Error('Token invalid');
          }).then(data => {
            if (!isCancelled) {
              setUser(data.user);
              localStorage.setItem(USER_KEY, JSON.stringify(data.user));
              localCacheService.setCurrentUser(data.user.id);
              setLoading(false);
            }
          }).catch(err => {
            console.warn('自动登录验证失败:', err);
            if (!isCancelled) {
              logout();
              setLoading(false);
            }
          });
        } catch (e) {
          console.error('恢复登录状态失败:', e);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
    
    // 🆕 清理函数
    return () => {
      isCancelled = true;
    };
  }, [logout]);

  // 登录
  const login = useCallback(async (username, password) => {
    const res = await fetch(`${C.API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || '登录失败');
    }
    
    // 🆕 设置缓存服务的当前用户
    localCacheService.setCurrentUser(data.user.id);
    
    // 保存到状态和 localStorage
    setUser(data.user);
    setToken(data.token);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    
    return data;
  }, []);

  // 注册
  const register = useCallback(async (username, password, inviteCode, phone, smsCode) => {
    const payload = { username, password, inviteCode };
    if (phone) payload.phone = phone;
    if (smsCode) payload.smsCode = smsCode;
    const res = await fetch(`${C.API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || '注册失败');
    }
    
    // 🆕 设置缓存服务的当前用户
    localCacheService.setCurrentUser(data.user.id);
    
    // 注册成功后自动登录
    setUser(data.user);
    setToken(data.token);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    
    return data;
  }, []);

  // 刷新 token
  const refreshToken = useCallback(async () => {
    if (!token) return null;
    
    try {
      const res = await fetch(`${C.API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setToken(data.token);
        // 🆕 根据模式选择存储
        const storage = getStorage();
        storage.setItem(TOKEN_KEY, data.token);
        return data.token;
      }
    } catch (e) {
      console.error('刷新 token 失败:', e);
    }
    
    return null;
  }, [token]);

  // 🆕 刷新用户信息（头像更新后调用）
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    
    try {
      const res = await fetch(`${C.API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        // 🆕 根据模式选择存储
        const storage = getStorage();
        storage.setItem(USER_KEY, JSON.stringify(data.user));
        return data.user;
      }
    } catch (e) {
      console.error('刷新用户信息失败:', e);
    }
    
    return null;
  }, [token]);

  // 获取带认证的 fetch 函数
  const authFetch = useCallback(async (url, options = {}) => {
    const headers = {
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const res = await fetch(url, { ...options, headers });
    
    // 如果 401，可能是 token 过期
    if (res.status === 401) {
      // 尝试刷新 token
      const newToken = await refreshToken();
      if (newToken) {
        // 用新 token 重试
        headers['Authorization'] = `Bearer ${newToken}`;
        return fetch(url, { ...options, headers });
      } else {
        // 刷新失败，登出
        logout();
      }
    }
    
    return res;
  }, [token, refreshToken, logout]);

  // 🔑 细粒度权限判断（与后端 requirePermission 逻辑一致）
  // super_admin 全通过；admin: permissions=null 表示旧账号/全部权限，数组则按包含判断；其他角色一律 false
  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.role !== 'admin') return false;
    const perms = user.permissions;
    if (perms === null || perms === undefined) return true; // 向后兼容：旧 admin 保留全部权限
    return Array.isArray(perms) && perms.includes(perm);
  }, [user]);

  // 🎖️ 会员功能判断：完全按 user.member_features（后端按会员等级解析下发）。
  // 不给 admin/super_admin 开后门，能否使用由会员等级决定（与后端一致）。
  const hasMemberFeature = useCallback((feature) => {
    if (!user) return false;
    return Array.isArray(user.member_features) && user.member_features.includes(feature);
  }, [user]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    hasPermission, // 🔑 细粒度权限判断
    membershipLevel: user?.membership_level || 'education', // 🎖️ 会员等级
    hasMemberFeature, // 🎖️ 会员功能判断
    isSessionMode, // 🆕 会话模式（管理员登录到用户）
    login,
    register,
    logout,
    refreshToken,
    refreshUser, // 🆕
    authFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth - 获取认证状态的 Hook
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
